import type { EmailTemplate } from '../types/platform';

/**
 * The deliverable email template set. Every template links to an authenticated
 * portal destination; none carries a candidate document or its contents.
 */
export const emailTemplates: EmailTemplate[] = [
  {
    id: 'tpl-registration-confirmation',
    type: 'registration_confirmation',
    name: 'Registration confirmation',
    subject: 'Your Velnoris Executive Network registration',
    category: 'transactional',
    updatedAt: '2026-08-02T10:00:00Z',
    body:
      'Dear {{preferredName}},\n\nThank you for registering with Velnoris Executive Network. Your account has been created.\n\nWhat happens next: a consultant from the relevant practice will review your profile. Registration places you in our research network; it is not an application and does not guarantee consideration for any particular mandate.\n\nYou control what is shared. Nothing about you is shared with any employer without your specific authorization for that mandate.\n\nSign in: {{portalUrl}}\n\nVelnoris Executive Network',
  },
  {
    id: 'tpl-email-verification',
    type: 'email_verification',
    name: 'Email verification',
    subject: 'Verify your email address',
    category: 'transactional',
    updatedAt: '2026-08-02T10:00:00Z',
    body:
      'Dear {{preferredName}},\n\nPlease verify your email address to activate your account.\n\nVerify: {{verificationUrl}}\n\nThis link expires in {{expiryHours}} hours. If you did not create an account, no action is required.\n\nWe will never ask you for a password, banking details or identity documents by email.\n\nVelnoris Executive Network',
  },
  {
    id: 'tpl-password-reset',
    type: 'password_reset',
    name: 'Password reset',
    subject: 'Reset your password',
    category: 'transactional',
    updatedAt: '2026-08-02T10:00:00Z',
    body:
      'Dear {{preferredName}},\n\nA password reset was requested for your account.\n\nReset: {{resetUrl}}\n\nThis link expires in {{expiryMinutes}} minutes and can be used once. If you did not request this, you can ignore this message; your password has not changed.\n\nVelnoris Executive Network',
  },
  {
    id: 'tpl-document-received',
    type: 'document_received',
    name: 'Document received',
    subject: '{{documentCategory}} received',
    category: 'transactional',
    updatedAt: '2026-08-02T10:00:00Z',
    body:
      'Dear {{preferredName}},\n\nWe have received your {{documentCategory}} and it is awaiting review.\n\nYour documents are held in private storage and are only ever shared with an employer when you specifically authorize it for a mandate.\n\nView in your document centre: {{portalUrl}}\n\nVelnoris Executive Network',
  },
  {
    id: 'tpl-document-review-completed',
    type: 'document_review_completed',
    name: 'Document review completed',
    subject: 'Document review completed',
    category: 'transactional',
    updatedAt: '2026-08-02T10:00:00Z',
    body:
      'Dear {{preferredName}},\n\nA consultant has completed the review of your {{documentCategory}}. The outcome is: {{reviewOutcome}}.\n\nAny feedback is recorded in your document centre with a specific observation and a suggested action for each item.\n\nView the feedback: {{portalUrl}}\n\nVelnoris Executive Network',
  },
  {
    id: 'tpl-revision-requested',
    type: 'revision_requested',
    name: 'Revision requested',
    subject: 'Document review completed — revision requested',
    category: 'transactional',
    updatedAt: '2026-08-14T09:00:00Z',
    body:
      'Dear {{preferredName}},\n\nA consultant has reviewed your {{documentCategory}} and requested a revision. {{itemCount}} specific items, each with a suggested action, are available in your document centre.\n\nThis review is part of our normal process. No paid service is required to act on it, and declining any optional service will never affect your candidacy.\n\nRead the feedback: {{portalUrl}}\n\nVelnoris Executive Network',
  },
  {
    id: 'tpl-opportunity-match',
    type: 'opportunity_match',
    name: 'Opportunity match',
    subject: 'A potential match in your area of expertise',
    category: 'marketing',
    updatedAt: '2026-08-14T09:00:00Z',
    body:
      'Dear {{preferredName}},\n\nA mandate in our {{practiceName}} practice may be a potential match for your profile.\n\nThis is a ranking indication produced by comparing your profile against the mandate requirements. It is not a determination that you are qualified, and consideration is not guaranteed.\n\nReview it: {{portalUrl}}\n\nYou can change what we notify you about, or stop these messages entirely, in your notification preferences: {{preferencesUrl}}\n\nVelnoris Executive Network',
  },
  {
    id: 'tpl-application-received',
    type: 'application_received',
    name: 'Application received',
    subject: 'We have received your application — {{applicationReference}}',
    category: 'transactional',
    updatedAt: '2026-08-02T10:00:00Z',
    body:
      'Dear {{preferredName}},\n\nWe have received your application for {{roleTitle}} (reference {{opportunityReference}}). Your application reference is {{applicationReference}}.\n\nA consultant will review your application. You can track its status at any time by signing in to your portal, and you may withdraw at any stage.\n\nWe do not attach documents to email. All materials are available through your portal.\n\nTrack your application: {{portalUrl}}\n\nVelnoris Executive Network',
  },
  {
    id: 'tpl-status-update',
    type: 'status_update',
    name: 'Application status update',
    subject: 'Update on your application — {{applicationReference}}',
    category: 'transactional',
    updatedAt: '2026-08-02T10:00:00Z',
    body:
      'Dear {{preferredName}},\n\nThe status of your application {{applicationReference}} has changed to: {{newStatus}}.\n\n{{statusExplanation}}\n\nEvery status on your application corresponds to a recorded action, with a date and the person responsible. You can see the full history in your portal.\n\nView your application: {{portalUrl}}\n\nVelnoris Executive Network',
  },
  {
    id: 'tpl-consent-request',
    type: 'consent_request',
    name: 'Presentation authorization request',
    subject: 'Your authorization is required before we present your profile',
    category: 'transactional',
    updatedAt: '2026-08-20T09:00:00Z',
    body:
      'Dear {{preferredName}},\n\nBefore your profile can be presented for {{opportunityReference}}, we need your specific authorization for this mandate.\n\n{{employerDisclosure}}\n\nWhat would be shared: {{documentsListed}}.\n\nYou may decline. Declining will not affect your candidacy, your standing in our network, or your access to any other opportunity.\n\nRespond in your portal: {{portalUrl}}\n\nVelnoris Executive Network',
  },
  {
    id: 'tpl-interview-request',
    type: 'interview_request',
    name: 'Interview request',
    subject: 'Interview requested — {{opportunityReference}}',
    category: 'transactional',
    updatedAt: '2026-08-02T10:00:00Z',
    body:
      'Dear {{preferredName}},\n\nThe client for reference {{opportunityReference}} has requested {{interviewStage}}.\n\nProposed times and a briefing note are available in your portal. If none of the proposed times work, you can propose alternatives.\n\nReasonable accommodations are available at any stage of the process. Tell us what you need and we will arrange it.\n\nConfirm your availability: {{portalUrl}}\n\nVelnoris Executive Network',
  },
  {
    id: 'tpl-new-message',
    type: 'new_message',
    name: 'New message',
    subject: 'New message from {{senderName}}',
    category: 'transactional',
    updatedAt: '2026-08-02T10:00:00Z',
    body:
      'Dear {{preferredName}},\n\nYou have a new message from {{senderName}} regarding {{threadSubject}}.\n\nMessages are held in the portal rather than sent by email, so that anything sensitive stays behind authentication.\n\nRead it: {{portalUrl}}\n\nVelnoris Executive Network',
  },
  {
    id: 'tpl-privacy-confirmation',
    type: 'privacy_request_confirmation',
    name: 'Privacy request confirmation',
    subject: 'Privacy request received — {{requestReference}}',
    category: 'transactional',
    updatedAt: '2026-08-02T10:00:00Z',
    body:
      'Dear {{preferredName}},\n\nWe have received your {{requestKind}} request. Your reference is {{requestReference}}.\n\nWe will confirm the outcome in writing by {{dueDate}}. If we need to verify your identity first, we will tell you exactly what is required and why.\n\nVelnoris Executive Network',
  },
];

export const getEmailTemplate = (id: string): EmailTemplate | undefined =>
  emailTemplates.find((t) => t.id === id);
